import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, MessageSquareLock, KeyRound, Copy, ShieldCheck, Lock, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import Reveal from "./Reveal";
import { useLang } from "@/i18n/LangContext";
import { CHANNELS, PGP_KEY, IMAGES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [asunto, setAsunto] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [anonimo, setAnonimo] = useState(true);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pgpOpen, setPgpOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!asunto.trim() || !mensaje.trim()) {
      toast.error(t("contact.toastRequired"));
      return;
    }
    setSending(true);
    setProgress(0);
    const timer = setInterval(() => {
      setProgress((p) => (p < 88 ? p + Math.random() * 14 : p));
    }, 120);
    try {
      await axios.post(`${API}/contact`, { asunto: asunto.trim(), mensaje: mensaje.trim(), anonimo });
      clearInterval(timer);
      setProgress(100);
      setTimeout(() => {
        setSending(false);
        setProgress(0);
        setAsunto("");
        setMensaje("");
        setAnonimo(true);
        toast.success(t("contact.toastOk"), {
          description: anonimo ? t("contact.toastOkAnon") : t("contact.toastOkNamed"),
        });
      }, 500);
    } catch (err) {
      clearInterval(timer);
      setSending(false);
      setProgress(0);
      toast.error(t("contact.toastErr"));
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(PGP_KEY);
      toast.success(t("contact.toastCopied"));
    } catch {
      toast.error(t("contact.toastCopyErr"));
    }
  };

  return (
    <section id="contacto" data-testid="contact-secure-section" className="relative py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={IMAGES.whisky}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-[0.06]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#111113] via-[#0D0D0E]/80 to-[#0D0D0E]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-16 max-w-2xl">
          <p className="font-code text-xs tracking-[0.35em] uppercase text-[#C5A059] mb-4">
            {t("contact.overline")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] tracking-tight">
            {t("contact.headingA")} <span className="italic text-[#D4AF37]">{t("contact.headingB")}</span>
          </h2>
          <p className="mt-5 text-[#A0A0A5] leading-relaxed">
            {t("contact.intro")}
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-[#141417]/90 backdrop-blur border border-[#F4F1EA]/10 p-8 lg:p-10"
            >
              <div className="flex items-center gap-3 mb-8">
                <Lock className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-code text-[11px] tracking-[0.3em] uppercase text-[#C5A059]">
                  {t("contact.secure")}
                </span>
              </div>

              <label className="block mb-6">
                <span className="block font-code text-[11px] tracking-[0.25em] uppercase text-[#8E8E93] mb-2.5">
                  {t("contact.subject")}
                </span>
                <input
                  data-testid="contact-subject-input"
                  type="text"
                  value={asunto}
                  onChange={(e) => setAsunto(e.target.value)}
                  placeholder={t("contact.subjectPh")}
                  className="w-full bg-[#0D0D0E] border border-[#F4F1EA]/15 focus:border-[#D4AF37]/60 outline-none px-4 py-3.5 text-sm text-[#F4F1EA] placeholder:text-[#5a5a5e] transition-colors duration-300"
                />
              </label>

              <label className="block mb-6">
                <span className="block font-code text-[11px] tracking-[0.25em] uppercase text-[#8E8E93] mb-2.5">
                  {t("contact.message")}
                </span>
                <textarea
                  data-testid="contact-message-input"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={7}
                  placeholder={t("contact.messagePh")}
                  className="w-full bg-[#0D0D0E] border border-[#F4F1EA]/15 focus:border-[#D4AF37]/60 outline-none px-4 py-3.5 font-code text-sm text-[#D4AF37] placeholder:text-[#5a5a5e] placeholder:font-sans resize-none transition-colors duration-300"
                />
              </label>

              <label
                className="flex items-start gap-3 mb-8 cursor-pointer group"
                data-testid="contact-anonymous-option"
              >
                <Checkbox
                  data-testid="contact-anonymous-checkbox"
                  checked={anonimo}
                  onCheckedChange={(v) => setAnonimo(!!v)}
                  className="mt-0.5 border-[#D4AF37]/50 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#0D0D0E]"
                />
                <span className="text-sm text-[#A0A0A5] group-hover:text-[#D8D4C8] transition-colors duration-300">
                  {t("contact.anonA")} <span className="text-[#D4AF37]">{t("contact.anonB")}</span>
                  <span className="block text-xs text-[#8E8E93] mt-1">
                    {t("contact.anonSub")}
                  </span>
                </span>
              </label>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="relative w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#E5C158] disabled:opacity-80 transition-colors duration-300 overflow-hidden"
              >
                {sending && (
                  <span
                    className="absolute inset-y-0 left-0 bg-[#0D0D0E]/15 transition-[width] duration-150"
                    style={{ width: `${progress}%` }}
                  />
                )}
                <ShieldCheck className="w-4 h-4 relative" />
                <span className="relative" data-testid="contact-submit-label">
                  {sending ? `${t("contact.encrypting")} ${Math.min(100, Math.round(progress))}%` : t("contact.submit")}
                </span>
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="space-y-6">
            <div className="bg-[#141417]/90 backdrop-blur border border-[#F4F1EA]/10 p-8" data-testid="channels-panel">
              <h3 className="font-display text-2xl text-[#F4F1EA] mb-7">{t("contact.channelsTitle")}</h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-code text-[11px] tracking-[0.25em] uppercase text-[#8E8E93] mb-1">
                      {t("contact.proton")}
                    </p>
                    <a
                      data-testid="channel-protonmail"
                      href={`mailto:${CHANNELS.proton}`}
                      className="font-code text-sm text-[#D4AF37] hover:text-[#E5C158] break-all transition-colors duration-300"
                    >
                      {CHANNELS.proton}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Send className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-code text-[11px] tracking-[0.25em] uppercase text-[#8E8E93] mb-1">
                      {t("contact.telegram")}
                    </p>
                    <a
                      data-testid="channel-telegram"
                      href={`https://${CHANNELS.telegram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-code text-sm text-[#D4AF37] hover:text-[#E5C158] transition-colors duration-300"
                    >
                      {CHANNELS.telegram}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MessageSquareLock className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <p className="font-code text-[11px] tracking-[0.25em] uppercase text-[#8E8E93] mb-1">
                      Session ID
                    </p>
                    <p data-testid="channel-session" className="font-code text-xs text-[#D8D4C8] break-all leading-relaxed">
                      {CHANNELS.session}
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <Collapsible open={pgpOpen} onOpenChange={setPgpOpen}>
              <div className="bg-[#141417]/90 backdrop-blur border border-[#F4F1EA]/10">
                <CollapsibleTrigger
                  data-testid="pgp-toggle"
                  className="w-full flex items-center justify-between p-8 group"
                >
                  <span className="flex items-center gap-3">
                    <KeyRound className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-display text-xl text-[#F4F1EA] group-hover:text-[#E5C158] transition-colors duration-300">
                      {t("contact.pgp")}
                    </span>
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-[#D4AF37] transition-transform duration-300 ${pgpOpen ? "rotate-180" : ""}`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent data-testid="pgp-key-block" className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                  <div className="px-8 pb-8">
                    <pre className="bg-[#0D0D0E] border border-[#D4AF37]/20 p-5 font-code text-[11px] leading-relaxed text-[#D4AF37] overflow-x-auto whitespace-pre">
                      {PGP_KEY}
                    </pre>
                    <button
                      data-testid="pgp-copy-button"
                      onClick={copyKey}
                      className="mt-4 flex items-center gap-2.5 px-5 py-3 gold-hairline text-[#D4AF37] text-xs tracking-[0.18em] uppercase hover:bg-[#D4AF37]/10 transition-colors duration-300"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      {t("contact.copy")}
                    </button>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>

            <p className="text-xs text-[#8E8E93] leading-relaxed pl-1 border-l border-[#D4AF37]/30 ml-1 py-1 px-4">
              {t("contact.note")}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
